/**
 * How wide the docked assistant may be beside the icon nav rail at `lg` and up, where a user can drag
 * it wider or narrower (#7205).
 *
 * Only there. Between `md` and `lg` it keeps a fixed 360px column: at 900–1200px a page beside a wider
 * assistant falls under the width it needs, and a narrower one would not fit a cited passage. Below
 * `md` it fills the window, so there is no edge to drag.
 */

/** The width it opens at, and goes back to on a double-click of its edge: what it always was. */
export const ASSISTANT_DEFAULT_WIDTH = 400;

/** Below this a result table loses its second column and a citation wraps every few words. */
export const ASSISTANT_MIN_WIDTH = 360;

/**
 * The narrowest the page beside it may become. A proposal's fee table and the work records table
 * scroll sideways below this, rather than compacting, so this is where the assistant stops taking room.
 */
export const PAGE_MIN_WIDTH = 600;

/** Never more than half of the row, so on a very wide monitor the page is still the main thing. */
export const ASSISTANT_MAX_SHARE_OF_ROW = 0.5;

/** How far an arrow key moves the edge; Shift moves it further. */
export const ASSISTANT_WIDTH_STEP = 16;
export const ASSISTANT_WIDTH_LARGE_STEP = 48;

export type AssistantWidthLimits = { min: number; max: number };

/**
 * The range the assistant may take, measured from the row it sits in.
 *
 * `rowWidth` is the whole row: the rail, the assistant and the page. `sharedWidth` is what the
 * assistant and the page share between them, which is the row less the rail. The maximum never falls
 * below the minimum, so a window too narrow for both keeps the assistant at its minimum.
 */
export function assistantWidthLimits(rowWidth: number, sharedWidth: number): AssistantWidthLimits {
  const max = Math.min(Math.floor(rowWidth * ASSISTANT_MAX_SHARE_OF_ROW), Math.floor(sharedWidth - PAGE_MIN_WIDTH));
  return { min: ASSISTANT_MIN_WIDTH, max: Math.max(ASSISTANT_MIN_WIDTH, max) };
}

export function clampAssistantWidth(width: number, limits: AssistantWidthLimits): number {
  return Math.min(Math.max(Math.round(width), limits.min), limits.max);
}
